import React, { useState, useEffect } from 'react';
import { notificationsAPI } from '../../services/api';
import Toast from '../../components/common/Toast';

const BroadcastForm = () => {
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'engagement',
    target_type: 'all',
    target_criteria: {},
    scheduled_at: '',
    data: {}
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const response = await notificationsAPI.getTemplates();
      if (response.data.success) {
        setTemplates(response.data.data.templates);
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleTemplateSelect = (e) => {
    const templateId = e.target.value;
    if (!templateId) return;

    const template = templates.find(t => t.id === parseInt(templateId));
    if (template) {
      setFormData({
        ...formData,
        title: template.title,
        message: template.message,
        type: template.type,
        data: template.data || {}
      });
    }
  };

  const handleTargetCriteriaChange = (key, value) => {
    setFormData({
      ...formData,
      target_criteria: { ...formData.target_criteria, [key]: value }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Clean up data based on target_type
      const payload = {
        title: formData.title,
        message: formData.message,
        type: formData.type,
        target_type: formData.target_type,
      };

      if (formData.target_type === 'segment') {
        payload.target_criteria = formData.target_criteria;
      }

      if (formData.scheduled_at) {
        payload.scheduled_at = formData.scheduled_at;
      }

      if (Object.keys(formData.data).length > 0) {
        payload.data = formData.data;
      }

      const response = await notificationsAPI.broadcast(payload);

      if (response.data.success) {
        setToast({
          show: true,
          message: formData.scheduled_at
            ? 'Broadcast scheduled successfully!'
            : `Broadcast sent successfully! Delivered to ${response.data.data.total_sent || 0} users`,
          type: 'success'
        });

        // Reset form
        setFormData({
          title: '',
          message: '',
          type: 'engagement',
          target_type: 'all',
          target_criteria: {},
          scheduled_at: '',
          data: {}
        });
      }
    } catch (error) {
      setToast({
        show: true,
        message: error.response?.data?.message || 'Failed to send broadcast',
        type: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />

      <div className="page-header mb-4">
        <div>
          <h2 className="page-title">Send Notification</h2>
          <p className="page-subtitle">Broadcast messages to app users</p>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-8">
          <div className="stat-card">
            <form onSubmit={handleSubmit}>
              <div className="row">
                {/* Template Selector */}
                <div className="col-12 mb-3">
                  <label className="form-label">Use Template (Optional)</label>
                  <select className="form-select" onChange={handleTemplateSelect} disabled={loadingTemplates}>
                    <option value="">-- Select a template --</option>
                    {templates.map(template => (
                      <option key={template.id} value={template.id}>
                        {template.name} ({template.type})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Title */}
                <div className="col-12 mb-3">
                  <label className="form-label">Title *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="e.g., Don't forget your prayers today!"
                    required
                    maxLength="255"
                  />
                </div>

                {/* Message */}
                <div className="col-12 mb-3">
                  <label className="form-label">Message *</label>
                  <textarea
                    className="form-control"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="4"
                    placeholder="Enter your message here..."
                    required
                    maxLength="1000"
                  ></textarea>
                  <small className="text-muted">{formData.message.length}/1000 characters</small>
                </div>

                {/* Notification Type */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">Notification Type *</label>
                  <select
                    className="form-select"
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    required
                  >
                    <option value="engagement">Engagement</option>
                    <option value="prayer">Prayer Reminder</option>
                    <option value="habit">Habit Reminder</option>
                    <option value="cycle">Cycle Update</option>
                    <option value="qada">Qada Reminder</option>
                    <option value="insight">Daily Insight</option>
                    <option value="spiritual">Spiritual Reminder</option>
                    <option value="system">System Announcement</option>
                  </select>
                </div>

                {/* Target Audience */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">Target Audience *</label>
                  <select
                    className="form-select"
                    name="target_type"
                    value={formData.target_type}
                    onChange={handleChange}
                    required
                  >
                    <option value="all">All Users</option>
                    <option value="segment">Specific Segment</option>
                  </select>
                </div>

                {/* Segment Filters */}
                {formData.target_type === 'segment' && (
                  <>
                    <div className="col-12 mb-2">
                      <label className="form-label fw-bold">Segment Filters</label>
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">Madhab</label>
                      <select
                        className="form-select"
                        value={formData.target_criteria.madhab || ''}
                        onChange={(e) => handleTargetCriteriaChange('madhab', e.target.value)}
                      >
                        <option value="">All Madhabs</option>
                        <option value="Hanafi">Hanafi</option>
                        <option value="Maliki">Maliki</option>
                        <option value="Shafi'i">Shafi'i</option>
                        <option value="Hanbali">Hanbali</option>
                        <option value="Jafari">Jafari</option>
                      </select>
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">Country</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.target_criteria.country || ''}
                        onChange={(e) => handleTargetCriteriaChange('country', e.target.value)}
                        placeholder="e.g., Nigeria"
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">Premium Users Only</label>
                      <select
                        className="form-select"
                        value={formData.target_criteria.is_premium || ''}
                        onChange={(e) => handleTargetCriteriaChange('is_premium', e.target.value)}
                      >
                        <option value="">All Users</option>
                        <option value="1">Premium Only</option>
                        <option value="0">Free Only</option>
                      </select>
                    </div>
                  </>
                )}

                {/* Schedule */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">Schedule (Optional)</label>
                  <input
                    type="datetime-local"
                    className="form-control"
                    name="scheduled_at"
                    value={formData.scheduled_at}
                    onChange={handleChange}
                  />
                </div>
                <small className="text-muted mb-3">Leave empty to send immediately</small>

                {/* Submit Button */}
                <div className="col-12">
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-send me-2"></i>
                        {formData.scheduled_at ? 'Schedule Broadcast' : 'Send Now'}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Preview Card */}
        <div className="col-lg-4">
          <div className="stat-card">
            <h5 className="mb-3">Preview</h5>
            <div className="notification-preview p-3 border rounded">
              <div className="d-flex align-items-start gap-3">
                <div className="notification-icon bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                  <i className="bi bi-bell-fill"></i>
                </div>
                <div className="flex-grow-1">
                  <h6 className="mb-1">{formData.title || 'Notification Title'}</h6>
                  <p className="mb-1 text-muted small">{formData.message || 'Your message will appear here...'}</p>
                  <small className="text-muted">Just now</small>
                </div>
              </div>
            </div>

            <div className="mt-3">
              <small className="text-muted d-block mb-1">
                <strong>Type:</strong> {formData.type}
              </small>
              <small className="text-muted d-block mb-1">
                <strong>Target:</strong> {formData.target_type === 'all' ? 'All Users' : 'Filtered Segment'}
              </small>
              {formData.scheduled_at && (
                <small className="text-muted d-block">
                  <strong>Scheduled:</strong> {new Date(formData.scheduled_at).toLocaleString()}
                </small>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BroadcastForm;